import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, UserPlus, AlertTriangle, Clock, ClipboardCheck, ShieldAlert, CheckCircle2,
  Check, Inbox, ArrowRight,
} from "lucide-react";
import api from "@/lib/api";
import { fmtNotifTime } from "@/lib/format";

// type -> { icon, tint } — calm, premium palette, no playful colors.
export const NOTIF_META = {
  lead_assigned: { icon: UserPlus, tint: "text-loop bg-loop/10" },
  lead_reassigned: { icon: UserPlus, tint: "text-loop bg-loop/10" },
  sla_breach: { icon: AlertTriangle, tint: "text-red-600 bg-red-50" },
  followup_due: { icon: Clock, tint: "text-amber-600 bg-amber-50" },
  review_item: { icon: ClipboardCheck, tint: "text-violet-600 bg-violet-50" },
  system_alert: { icon: ShieldAlert, tint: "text-red-600 bg-red-50" },
  import_done: { icon: CheckCircle2, tint: "text-emerald-600 bg-emerald-50" },
};
const fallback = { icon: Bell, tint: "text-ink/60 bg-mist" };

export function NotifIcon({ type, className = "" }) {
  const m = NOTIF_META[type] || fallback;
  const Icon = m.icon;
  return (
    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${m.tint} ${className}`}>
      <Icon className="h-[18px] w-[18px]" />
    </span>
  );
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pulse, setPulse] = useState(false);
  const ref = useRef(null);
  const prevUnread = useRef(0);
  const closeRef = useRef(() => {});
  closeRef.current = () => setOpen(false);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      const c = data.data.count;
      if (c > prevUnread.current) { setPulse(true); setTimeout(() => setPulse(false), 1200); }
      prevUnread.current = c;
      setUnread(c);
    } catch { /* silent */ }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/notifications", { params: { page_size: 8 } }); setItems(data.data.items); setUnread(data.data.unread); prevUnread.current = data.data.unread; }
    catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCount(); const t = setInterval(loadCount, 45000); return () => clearInterval(t); }, [loadCount]);
  useEffect(() => { if (open) loadList(); }, [open, loadList]);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) closeRef.current(); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const onItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      setItems((p) => p.map((x) => x.id === n.id ? { ...x, read: true } : x));
      setUnread((u) => Math.max(0, u - 1));
      api.post(`/notifications/${n.id}/read`).catch(() => {});
    }
    if (n.route) navigate(n.route);
  };

  const markAll = async () => {
    setItems((p) => p.map((x) => ({ ...x, read: true }))); setUnread(0);
    await api.post("/notifications/read-all").catch(() => {});
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notifications-button"
        onClick={() => setOpen((v) => !v)}
        className={`relative rounded-xl border p-2 text-ink/70 transition-colors hover:bg-mist ${open ? "border-loop bg-loop/5" : "border-sky"}`}
      >
        <Bell className={`h-[18px] w-[18px] ${pulse ? "animate-[wiggle_0.5s_ease-in-out]" : ""}`} />
        {unread > 0 && (
          <span data-testid="notif-unread-badge" className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white ring-2 ring-white">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-dropdown"
          className="absolute right-0 z-50 mt-2 w-[360px] origin-top-right overflow-hidden rounded-2xl border border-sky/70 bg-white shadow-2xl shadow-navy/10 animate-[notifIn_0.16s_ease-out]"
        >
          <div className="flex items-center justify-between border-b border-sky/60 px-4 py-3">
            <div className="flex items-center gap-2">
              <h3 className="font-sora text-sm font-semibold text-navy">Notifications</h3>
              {unread > 0 && <span className="rounded-full bg-loop/10 px-2 py-0.5 text-[11px] font-bold text-loop">{unread} new</span>}
            </div>
            {unread > 0 && (
              <button onClick={markAll} data-testid="notif-mark-all" className="flex items-center gap-1 text-xs font-medium text-loop transition-colors hover:text-loophover">
                <Check className="h-3.5 w-3.5" />Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {loading ? (
              <div className="space-y-1 p-2">
                {[0, 1, 2].map((i) => <div key={i} className="flex gap-3 rounded-xl p-2.5"><div className="h-9 w-9 animate-pulse rounded-xl bg-mist" /><div className="flex-1 space-y-2 py-1"><div className="h-2.5 w-3/4 animate-pulse rounded bg-mist" /><div className="h-2 w-1/2 animate-pulse rounded bg-mist" /></div></div>)}
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
                <Inbox className="mb-2 h-8 w-8 text-ink/25" />
                <p className="text-sm font-medium text-navy">You&apos;re all caught up</p>
                <p className="text-xs text-ink/45">New alerts will show up here.</p>
              </div>
            ) : (
              <ul className="p-1.5">
                {items.map((n, i) => (
                  <li key={n.id}>
                    <button
                      data-testid={`notif-item-${n.id}`}
                      onClick={() => onItem(n)}
                      style={{ animationDelay: `${i * 24}ms` }}
                      className={`group flex w-full animate-[notifIn_0.2s_ease-out_both] items-start gap-3 rounded-xl px-2.5 py-2.5 text-left transition-colors hover:bg-mist ${n.read ? "" : "bg-loop/[0.04]"}`}
                    >
                      <NotifIcon type={n.type} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`truncate text-sm ${n.read ? "font-medium text-ink/80" : "font-semibold text-navy"}`}>{n.title}</p>
                          {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-loop" />}
                        </div>
                        {n.body && <p className="mt-0.5 line-clamp-2 text-xs text-ink/55">{n.body}</p>}
                        <p className="mt-1 text-[11px] text-ink/40">{fmtNotifTime(n.created_at)}</p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button
            data-testid="notif-see-all"
            onClick={() => { setOpen(false); navigate("/notifications"); }}
            className="flex w-full items-center justify-center gap-1.5 border-t border-sky/60 py-3 text-sm font-medium text-loop transition-colors hover:bg-loop/5"
          >
            See all notifications<ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
